import type { ComponentEventHandler } from '@archipro-design/aria';
import {
    Button,
    NumberInput,
    useStyles,
    useTheme,
} from '@archipro-design/aria';
import { HeartFill, HeartLine } from '@archipro-design/icons';
import * as S from './QuantityGroup.style';
import { PlusLight, LightMinus } from '@archipro-design/icons';
import { useVariantSelectionState } from '@modules/product/context/VariantSelectionContext';
import { useVariantStock } from '../../hook/use-variant-stock';
import { useAddToCartWithTracking } from '../../hook/use-add-to-cart-with-tracking';
import { FollowSelector } from '~/modules/moodboards/components';
import { useLoaderData } from 'react-router';
import type { ProductPageLoaderData } from '~/routes/_app.product.$urlSegment';
import { useFavouriteVariant } from '../../hook/use-favourite-variant';
import type { CartItem } from '@modules/ecommerce/type/cart-item';
import { useVariantShipping } from '~/modules/product/hook/use-variant-shipping';
import { useHandleFollow } from '~/modules-rocco/design-board/hook/use-handle-follow';

interface PurchaseButtonGroupProps {
    onItemAdded?: (item: CartItem | null) => void;
}

const PurchaseButtonGroup = ({ onItemAdded }: PurchaseButtonGroupProps) => {
    const { quantity, setQuantity, selectedVariant } =
        useVariantSelectionState();
    const { hasClickAndCollect, hasShippingType } = useVariantShipping();
    const { canAddToCart, quantityLimit } = useVariantStock({
        hasClickAndCollect,
        hasShippingType,
    });

    const { product } = useLoaderData<ProductPageLoaderData>();
    const { addSelectedToCart, isLoading } = useAddToCartWithTracking({
        onItemAdded,
    });
    const favouriteItem = useFavouriteVariant(product.ID);
    const { isInDesignBoard } = useHandleFollow();

    const { css } = useStyles({ canAddToCart });
    const {
        siteVariables: { colors },
    } = useTheme();

    const onInputChange: ComponentEventHandler<number | null> = (_, data) => {
        const newQuantity = data ?? -1;
        setQuantity(newQuantity);
    };

    const onInputBlur = () => {
        if (quantity < 1) {
            setQuantity(1);
        }
    };
    const onAddToCartClick = () => {
        addSelectedToCart();
    };

    return (
        <div className={css(S.QuantityGroup)}>
            <div className={`${css(S.PurchaseGroup)} purchase-group`}>
                <Button
                    className={css(S.ButtonBase, S.ButtonSubstract)}
                    icon={<LightMinus />}
                    transparent
                    size={24}
                    variables={{
                        primaryButtonBackgroundColorNormal:
                            colors.white['100A'],
                        buttonPadding: 0,
                        iconPadding: 0,
                    }}
                    disabled={
                        !selectedVariant || !canAddToCart || quantity <= 1
                    }
                    onClick={() => setQuantity(quantity - 1)}
                />
                <NumberInput
                    className={css(S.BoxInput)}
                    disabled={!canAddToCart}
                    value={quantity < 0 ? null : quantity}
                    onChange={onInputChange}
                    min={0}
                    max={quantityLimit}
                    onBlur={onInputBlur}
                    maxLength={quantityLimit.toString().length}
                    allowsEmpty={true}
                />
                <Button
                    className={css(S.ButtonBase, S.ButtonAdd)}
                    icon={<PlusLight />}
                    transparent
                    size={24}
                    variables={{
                        primaryButtonBackgroundColorNormal:
                            colors.white['100A'],
                        buttonPadding: 0,
                        iconPadding: 0,
                    }}
                    disabled={
                        !selectedVariant ||
                        !canAddToCart ||
                        quantity >= quantityLimit
                    }
                    onClick={() => setQuantity(quantity + 1)}
                />
                <Button
                    className={css(S.ButtonAddToCart)}
                    disabled={!canAddToCart}
                    size={16}
                    fluid
                    onClick={onAddToCartClick}
                    loading={isLoading}
                    variables={{
                        iconPadding: 0,
                        buttonPadding: 0,
                    }}
                >
                    Add to cart
                </Button>
                <FollowSelector
                    trigger={
                        <Button
                            className={css(
                                S.ButtonBase,
                                S.ButtonHeart,
                                S.SaveToDesignBoard,
                            )}
                            transparent
                            size={20}
                            onClick={(event) => {
                                event.stopPropagation();
                            }}
                            icon={
                                isInDesignBoard(
                                    favouriteItem.pinId,
                                    favouriteItem.pinType,
                                ) ? (
                                    <HeartFill size="2x" />
                                ) : (
                                    <HeartLine size="2x" />
                                )
                            }
                            variables={{
                                primaryButtonBackgroundColorNormal:
                                    colors.white['100A'],
                                iconPadding: 0,
                                buttonPadding: 0,
                            }}
                        />
                    }
                    pinId={favouriteItem.pinId}
                    pinType={favouriteItem.pinType}
                />
            </div>
        </div>
    );
};

export default PurchaseButtonGroup;
